import {
  defModule,
  Task,
  type TaskDeclaration,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import { compareVersions } from '@st/util/app-version'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import {
  BrowserPage,
  ErrorReporting,
  RuntimeConfig,
  maintainSocketConnection,
  observeSocketTopic,
  type SocketTopicEvent
} from '~/effects'
import { observeCurrentUser } from '~/features/module-runtime'

type SocketSessionState = {
  currentUserId: string | null
}

type SocketSessionMessage =
  | TaskMessage<typeof observeCurrentUser>
  | { type: 'userVersionInfoReceived'; minClientVersion: string; apiVersion: string }

type SocketSessionTask = TaskDeclaration<SocketSessionMessage>
type SocketSessionUpdate = UpdateResult<SocketSessionState, SocketSessionMessage>

const keepSocketConnected = Task.define((_currentUserId: string) => maintainSocketConnection)

const observeUserVersion = Task.define((_currentUserId: string) =>
  Task.pipe(observeSocketTopic('user'), Task.map(userVersionMessage))
)

export const socketSessionModule = defModule<{
  state: SocketSessionState
  message: SocketSessionMessage
}>({
  init: () => ({ currentUserId: null }),
  tasks: (state) => [observeCurrentUser(), ...authenticatedSocketTasks(state)],
  update: (state, message): SocketSessionUpdate =>
    match(message)
      .returnType<SocketSessionUpdate>()
      .with({ type: 'currentUserChanged' }, ({ currentUserId }) => {
        if (state.currentUserId == currentUserId) {
          return state
        }

        return { currentUserId }
      })
      .with({ type: 'userVersionInfoReceived' }, (version) => [
        state,
        enforceClientVersion(version)
      ])
      .exhaustive()
})

function authenticatedSocketTasks(state: SocketSessionState): SocketSessionTask[] {
  if (!state.currentUserId) {
    return []
  }

  return [keepSocketConnected(state.currentUserId), observeUserVersion(state.currentUserId)]
}

function userVersionMessage(event: SocketTopicEvent<'user'>): SocketSessionMessage | null {
  return match(event)
    .returnType<SocketSessionMessage | null>()
    .with({ type: 'messageReceived', message: { type: 'version_info' } }, ({ message }) => {
      const data = message.data
      if (
        typeof data != 'object' ||
        data == null ||
        !('min_client_version' in data) ||
        !('api_version' in data) ||
        typeof data.min_client_version != 'string' ||
        typeof data.api_version != 'string'
      ) {
        throw new Error('Malformed version_info socket message')
      }

      return {
        type: 'userVersionInfoReceived',
        minClientVersion: data.min_client_version,
        apiVersion: data.api_version
      }
    })
    .otherwise(() => null)
}

function enforceClientVersion(
  message: Extract<SocketSessionMessage, { type: 'userVersionInfoReceived' }>
) {
  return Effect.gen(function* () {
    const errorReporting = yield* ErrorReporting
    const page = yield* BrowserPage
    const runtime = yield* RuntimeConfig
    const clientVersion = runtime.clientVersion

    if (!clientVersion) {
      return null
    }

    yield* Effect.logInfo(
      `Client version ${clientVersion} connected to server ${message.apiVersion}`
    )
    if (compareVersions(clientVersion, message.minClientVersion) >= 0) {
      return null
    }

    yield* Effect.logInfo(
      `Client version is too old ${clientVersion} < ${message.minClientVersion} - reloading`
    )
    yield* page.alert(`Client version is too old ${clientVersion} < ${message.minClientVersion}`)

    yield* errorReporting.captureEvent({
      message: 'Client auto-updated',
      level: 'info',
      tags: {
        minRequiredClientVersion: message.minClientVersion,
        currentClientVersion: clientVersion
      }
    })

    yield* Effect.sleep(1000)
    yield* page.reload
    return null
  })
}
